/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import type { Ref } from 'react';
import AlertToastIcon from './AlertToastIcon';
import type { RetainedToast } from './toastHistoryStore';

interface Props {
  toast: RetainedToast | null;
  contentRef: Ref<HTMLElement>;
  onClose: () => void;
  onCopy: () => void;
  onReport: () => void;
}

/** Full text of a warning or error alert, with Copy and Report issue. */
export default function AlertDetailsDialog({
  toast,
  contentRef,
  onClose,
  onCopy,
  onReport,
}: Readonly<Props>) {
  const isString = typeof toast?.content === 'string';
  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={toast !== null}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', px: 3, pt: 2.5, pb: 1 }}>
        {toast && <AlertToastIcon type={toast.type} />}
        <Typography component="h2" sx={{ flex: 1, fontSize: 16, fontWeight: 600 }}>
          {toast?.type === 'warning' ? 'Warning' : 'Error'}
        </Typography>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Stack>
      <DialogContent sx={{ px: 3 }}>
        <Box
          ref={contentRef}
          component={isString ? 'pre' : 'div'}
          sx={{
            m: 0,
            px: 1.75,
            py: 1.5,
            maxHeight: '60vh',
            overflow: 'auto',
            borderRadius: '10px',
            border: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
            fontFamily: isString
              ? '"Geist Mono", ui-monospace, SFMono-Regular, Menlo, monospace'
              : 'inherit',
            fontSize: 12.5,
            lineHeight: '19px',
            overflowWrap: 'anywhere',
            whiteSpace: isString ? 'pre-wrap' : 'normal',
            wordBreak: 'break-word',
          }}
        >
          {toast?.content}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" startIcon={<ContentCopyRoundedIcon />} onClick={onCopy}>
          Copy
        </Button>
        <Button variant="outlined" startIcon={<OpenInNewRoundedIcon />} onClick={onReport}>
          Report issue
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" disableElevation onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
